import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Flame, Wallet, TrendingUp,
  Shield, Rocket,
} from 'lucide-react';
import WhitelistForm from '../components/WhitelistForm';

const LAUNCH_DATE = new Date('2026-10-15T00:00:00');

const getTimeLeft = () => {
  const diff = LAUNCH_DATE.getTime() - Date.now();
  const clamped = Math.max(diff, 0);
  return {
    days: Math.floor(clamped / (1000 * 60 * 60 * 24)),
    hours: Math.floor((clamped / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((clamped / (1000 * 60)) % 60),
    seconds: Math.floor((clamped / 1000) % 60),
    done: diff <= 0,
  };
};

const keyStats = [
  { v: '$0.005', l: 'Strategic Price' },
  { v: '$0.020', l: 'Public Price' },
  { v: '4×', l: 'Price Advantage' },
  { v: '$500K', l: 'Total Raise' },
  { v: '100M', l: 'AZT Tokens' },
  { v: '500,000', l: 'Fleet Target (2045)' },
];

const solves = [
  { title: 'Electric fleet operations', desc: '$6/day validated remittance' },
  { title: 'Battery-as-a-Service', desc: 'Swap in under 2 minutes, $1.00/swap' },
  { title: 'Ride-hailing', desc: 'Launching Phase 2, 2028' },
  { title: 'EV sales', desc: '20% gross margin' },
  { title: 'Carbon credits', desc: 'Gold Standard eligible at scale' },
  { title: 'Full OEM manufacturing', desc: '$2B–$5B+/yr by 2045' },
];

const reasons = [
  { icon: <TrendingUp size={18} />, title: '4× Price Advantage', desc: 'At $0.005, you enter at 4× below the public price of $0.020. This structural advantage is locked in from day one.' },
  { icon: <Wallet size={18} />, title: '10% Revenue Distribution', desc: "Azura's framework distributes 10% of all operational revenue to strategic investors, backed by deployed assets." },
  { icon: <Flame size={18} />, title: 'Token Burn = Scarcity', desc: '10% of revenue permanently burns tokens. Growing ecosystem plus falling supply means compounding value over time.' },
  { icon: <Shield size={18} />, title: 'Real Assets Backing', desc: '$500K funds 500 bikes, 17 BaaS stations, 510 batteries, and a tech platform, not a concept.' },
  { icon: <Rocket size={18} />, title: 'Ground Floor of $5B+', desc: 'Azura targets $2B–$5B+ manufacturing revenue by 2045. You are in at the very beginning.' },
];

const roadmap = [
  { phase: 'Phase 1', period: '2026–2032', name: 'Deploy & Operate', range: '$1M → $263M/yr' },
  { phase: 'Phase 2', period: '2033–2037', name: 'CKD Assembly', range: '$200M–$500M/yr' },
  { phase: 'Phase 3', period: '2038–2041', name: 'Partial OEM', range: '$500M–$1.5B/yr' },
  { phase: 'Phase 4', period: '2042–2045', name: 'Full OEM Export', range: '$2B–$5B+/yr' },
];

const projections = [
  { year: '2026', fleet: '500 bikes', fleetRev: '$799K', baasRev: '$263K', total: '$1.34M' },
  { year: '2028', fleet: '5,000 bikes', fleetRev: '$7.99M', baasRev: '$2.63M', total: '$13.2M' },
  { year: '2030', fleet: '20,000 bikes', fleetRev: '$31.96M', baasRev: '$10.5M', total: '$52.7M' },
  { year: '2036', fleet: '100,000 bikes', fleetRev: '$159.7M', baasRev: '$52.5M', total: '$263M' },
  { year: '2045', fleet: '500,000 bikes', fleetRev: '$798.5M', baasRev: '$262.7M', total: '$1.31B+' },
];

const tokenDetails = [
  { label: 'Total Supply', value: '1,000,000,000 AZT (fixed)' },
  { label: 'Token Type', value: 'Utility Token' },
  { label: 'Inflation', value: 'None' },
  { label: 'Burn', value: '10% of all revenue permanently burned' },
  { label: 'Investor Dist.', value: '10% of all revenue to strategic investors' },
  { label: 'Strategic Price', value: '$0.005 per AZT' },
  { label: 'Blockchain', value: 'TBD — announcement before launch' },
];

const useOfFunds = [
  { label: '500 Electric Motorcycles', value: '$1,000,000*' },
  { label: '17 BaaS Swap Stations', value: '$170,000' },
  { label: '510 Battery Units', value: '$357,000' },
  { label: 'Technology Platform', value: '$80,000' },
  { label: 'Operations & Setup', value: '$80,000' },
  { label: 'Contingency Reserve', value: '$63,000' },
];

const steps = [
  { num: '1', title: 'Connect your wallet', desc: 'MetaMask, WalletConnect, or Trust Wallet.' },
  { num: '2', title: 'Select the Strategic Round', desc: 'Priced at $0.005 per AZT.' },
  { num: '3', title: 'Enter your USD amount', desc: 'Minimum entry is $10.' },
  { num: '4', title: 'Confirm the transaction', desc: 'Review and approve in your wallet.' },
  { num: '5', title: 'AZT credited to your wallet', desc: "You're in, from the ground floor." },
];

/* ══ STRATEGIC ROUND PAGE ══ */
const StrategicRound: React.FC = () => {
  const [time, setTime] = useState(getTimeLeft());

  useEffect(() => {
    const id = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const units = [
    { label: 'Days', value: time.days },
    { label: 'Hours', value: time.hours },
    { label: 'Minutes', value: time.minutes },
    { label: 'Seconds', value: time.seconds },
  ];

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
            <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
            <span className="text-white/30">/</span>
            <span className="text-accent-amber text-xs font-semibold">Strategic Round</span>
          </div>

          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/10 px-4 py-1.5 rounded-full mb-6 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
            Investor One-Pager · 2026
          </span>

          <h1 className="text-4xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-6">
            Powering Africa's <span className="text-accent-amber">Electric Mobility</span> Future
          </h1>

          <p className="max-w-2xl text-white/70 text-base leading-8 mb-10">
            Azura Mobility is Nigeria's integrated electric vehicle fleet, Battery-as-a-Service, and ride-hailing company, on a phased path to becoming Africa's first indigenous EV manufacturer by 2042.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {keyStats.map(s => (
              <div key={s.l} className="bg-white/[0.06] border border-white/10 rounded-2xl px-4 py-5 text-center">
                <p className="font-figtree font-black text-xl md:text-2xl text-accent-amber leading-none mb-1.5">{s.v}</p>
                <p className="font-mono text-[9px] text-white/40 uppercase tracking-widest leading-snug">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ COUNTDOWN ══ */}
      <section className="py-16 px-6 bg-white border-b border-border">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Strategic Round Closes</span>
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-bg-dark tracking-tight mt-3 mb-10">
            Strategic round opens <span className="text-accent-amber italic">October 15, 2026.</span>
          </h2>

          <div className="grid grid-cols-4 gap-3 md:gap-5 max-w-xl mx-auto">
            {units.map(u => (
              <div key={u.label} className="bg-bg-dark rounded-2xl py-6 px-2 shadow-card">
                <p className="font-figtree font-black text-3xl md:text-5xl text-accent-amber tabular-nums">{String(u.value).padStart(2, '0')}</p>
                <p className="text-[10px] md:text-xs text-white/40 uppercase tracking-widest mt-2">{u.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ THE OPPORTUNITY ══ */}
      <section className="py-24 px-6 bg-neutral-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">The Opportunity</span>
            <h2 className="text-3xl md:text-4xl font-figtree font-black text-bg-dark tracking-tight mb-4">
              Africa's <span className="text-accent-amber italic">$6B+</span> electric mobility market
            </h2>
            <p className="text-neutral-600 font-exo text-base leading-relaxed">
              8 million+ commercial motorcycle operators in Nigeria alone. Fuel now consumes 30–50% of daily earnings after the 2023 subsidy removal. There is no affordable alternative, until Azura.
            </p>
            <p className="text-neutral-500 font-exo text-sm leading-relaxed mt-4">
              Azura solves this with:
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {solves.map(s => (
              <div key={s.title} className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-border shadow-card">
                <CheckCircle2 size={16} className="text-bg-dark shrink-0 mt-0.5" />
                <div>
                  <p className="font-figtree font-bold text-sm text-bg-dark leading-snug">{s.title}</p>
                  <p className="text-neutral-500 text-xs font-exo mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHY STRATEGIC ROUND ══ */}
      <section className="py-24 px-6 bg-bg-dark relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full border border-white/[0.05] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="mb-14">
            <span className="eyebrow-light">Why Strategic Round</span>
            <h2 className="text-3xl md:text-4xl font-figtree font-black text-white tracking-tight">
              5 reasons to <span className="text-accent-amber italic">invest now</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reasons.map((r, i) => (
              <div key={r.title} className={`p-7 bg-white/[0.04] border border-white/10 rounded-2xl hover:bg-white/[0.07] transition-all duration-300 ${i === 4 ? 'lg:col-span-1' : ''}`}>
                <div className="w-11 h-11 rounded-xl bg-accent-amber/10 border border-accent-amber/30 flex items-center justify-center text-accent-amber mb-5">
                  {r.icon}
                </div>
                <h3 className="font-figtree font-bold text-base text-white mb-2">{r.title}</h3>
                <p className="text-white/50 text-sm font-exo leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ MANUFACTURING ROADMAP ══ */}
      <section className="py-24 px-6 bg-white border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <span className="eyebrow">Manufacturing Roadmap</span>
            <h2 className="text-3xl md:text-4xl font-figtree font-black text-bg-dark tracking-tight">
              Deploy <ArrowRight size={22} className="inline mx-1 text-accent-amber" /> Assemble <ArrowRight size={22} className="inline mx-1 text-accent-amber" /> Partial OEM <ArrowRight size={22} className="inline mx-1 text-accent-amber" /> Full OEM
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {roadmap.map((p, i) => (
              <div key={p.phase} className={`relative rounded-2xl p-6 border overflow-hidden ${i === 3 ? 'bg-bg-dark border-accent-amber' : 'bg-neutral-50 border-border'}`}>
                <span className={`font-mono text-[10px] font-bold uppercase tracking-widest ${i === 3 ? 'text-accent-amber' : 'text-bg-dark/40'}`}>{p.phase}</span>
                <p className={`text-xs font-exo mb-4 ${i === 3 ? 'text-white/50' : 'text-neutral-500'}`}>{p.period}</p>
                <h3 className={`font-figtree font-black text-lg tracking-tight mb-2 ${i === 3 ? 'text-white' : 'text-bg-dark'}`}>{p.name}</h3>
                <p className={`font-figtree font-bold text-sm ${i === 3 ? 'text-accent-amber' : 'text-bg-dark/70'}`}>{p.range}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINANCIAL PROJECTIONS ══ */}
      <section className="py-24 px-6 bg-neutral-50 border-t border-border">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <span className="eyebrow">Financial Projections</span>
            <h2 className="text-3xl md:text-4xl font-figtree font-black text-bg-dark tracking-tight">
              Fleet + BaaS revenue, <span className="text-accent-amber italic">base case</span>
            </h2>
            <p className="text-neutral-500 font-exo text-sm mt-2">15% default buffer applied throughout.</p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-border shadow-card bg-white">
            <table className="w-full border-collapse min-w-[560px]">
              <thead>
                <tr className="bg-bg-dark">
                  {['Year', 'Fleet Size', 'Fleet Revenue', 'BaaS Revenue', 'Total Revenue'].map(h => (
                    <th key={h} className="px-5 py-4 text-left font-figtree text-[11px] font-bold uppercase tracking-widest text-white/60">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projections.map((row, i) => (
                  <tr key={row.year} className={`${i < projections.length - 1 ? 'border-b border-border' : ''} ${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}`}>
                    <td className="px-5 py-4 font-figtree font-bold text-sm text-bg-dark">{row.year}</td>
                    <td className="px-5 py-4 font-exo text-sm text-neutral-600">{row.fleet}</td>
                    <td className="px-5 py-4 font-exo text-sm text-neutral-600">{row.fleetRev}</td>
                    <td className="px-5 py-4 font-exo text-sm text-neutral-600">{row.baasRev}</td>
                    <td className="px-5 py-4 font-mono font-bold text-sm text-accent-amber-dim">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-neutral-400 font-exo text-xs mt-4 leading-relaxed">
            Excludes ride-hailing, carbon credits, and manufacturing revenue. Manufacturing adds $2B–$5B+ at full OEM scale.
          </p>
        </div>
      </section>

      {/* ══ AZT DETAILS + USE OF FUNDS ══ */}
      <section className="py-24 px-6 bg-white border-t border-border">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <span className="eyebrow">AZT Token Details</span>
            <h2 className="text-2xl md:text-3xl font-figtree font-black text-bg-dark tracking-tight mb-6">
              The structure, <span className="text-accent-amber italic">plainly stated.</span>
            </h2>
            <div className="rounded-2xl border border-border shadow-card overflow-hidden">
              {tokenDetails.map((d, i) => (
                <div key={d.label} className={`flex items-center justify-between gap-4 px-6 py-4 ${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} ${i < tokenDetails.length - 1 ? 'border-b border-border' : ''}`}>
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-bg-dark/40 shrink-0">{d.label}</span>
                  <span className="font-figtree font-bold text-sm text-bg-dark text-right">{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <span className="eyebrow">Use of Strategic Round Funds</span>
            <h2 className="text-2xl md:text-3xl font-figtree font-black text-bg-dark tracking-tight mb-6">
              Real assets, <span className="text-accent-amber italic">not a concept.</span>
            </h2>
            <div className="rounded-2xl border border-border shadow-card overflow-hidden">
              {useOfFunds.map((d, i) => (
                <div key={d.label} className={`flex items-center justify-between gap-4 px-6 py-4 ${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} border-b border-border`}>
                  <span className="font-exo text-sm text-neutral-600">{d.label}</span>
                  <span className="font-figtree font-bold text-sm text-bg-dark">{d.value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between gap-4 px-6 py-4 bg-bg-dark">
                <span className="font-figtree font-bold text-xs uppercase tracking-wider text-white/70">Total Phase 1 Capex (Strat+Presale)</span>
                <span className="font-figtree font-black text-lg text-accent-amber">$1.5M</span>
              </div>
            </div>
            <p className="text-neutral-400 font-exo text-xs mt-3">
              *Full bike capex covered when combined with Presale proceeds ($1M + $1.5M capex).
            </p>
          </div>
        </div>
      </section>

      {/* ══ HOW TO PARTICIPATE ══ */}
      <section className="py-24 px-6 bg-bg-dark text-white border-t-4 border-accent-amber">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 text-center">
            <span className="eyebrow-light justify-center flex">How to Participate</span>
            <h2 className="text-3xl md:text-4xl font-figtree font-black text-white tracking-tight">
              Five steps. <span className="text-accent-amber italic">Ground floor access.</span>
            </h2>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-5 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-accent-amber/25 to-transparent" />
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
              {steps.map(s => (
                <div key={s.num} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-accent-amber/10 border border-accent-amber/30 flex items-center justify-center mx-auto mb-4 font-mono font-bold text-accent-amber text-xs relative z-10 bg-bg-dark">
                    {s.num}
                  </div>
                  <h3 className="font-figtree font-bold text-sm text-white mb-1.5">{s.title}</h3>
                  <p className="text-white/50 text-xs font-exo leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ BUY CTA ══ */}
      <section className="px-6 py-16 bg-bg-cream">
        <div className="max-w-7xl mx-auto rounded-3xl bg-bg-dark px-8 py-10 md:py-12 flex flex-col md:flex-row items-center justify-between gap-6 border-4 border-accent-amber">
          <div className="text-center md:text-left">
            <p className="font-figtree font-black text-2xl md:text-3xl text-white tracking-tight">
              Ready to get in at <span className="text-accent-amber italic">$0.005?</span>
            </p>
            <p className="font-exo text-white/55 text-sm mt-1">The Strategic Round opens October 15, 2026. Secure your position from the ground floor.</p>
          </div>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm shrink-0">
            Buy AZT <ArrowRight size={13} />
          </Link>
        </div>
      </section>

      {/* ══ WHITELIST + WHITEPAPER ══ */}
      <WhitelistForm />

    </div>
  );
};

export default StrategicRound;
